import { useEffect, useRef, useState } from "react";
import { Pencil, Upload, Loader2 } from "lucide-react";
import { api, fileUrl, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { useSettings } from "@/contexts/SettingsContext";
import { useAdmin } from "@/contexts/AdminContext";

/**
 * Inline-editable text bound to a settings field.
 * Public visitors see plain text. Admins see a pencil affordance and can
 * tap to edit-in-place, then save (PATCH /settings).
 */
export function EditableText({
  field,
  value,
  multiline = false,
  className = "",
  placeholder = "",
  testId,
  as = "span",
}) {
  const { editing } = useAdmin();
  const { refresh } = useSettings();
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(value || "");
  const [saving, setSaving] = useState(false);

  useEffect(() => { setDraft(value || ""); }, [value]);

  const save = async () => {
    if (draft === value) { setOpen(false); return; }
    setSaving(true);
    try {
      await api.patch("/settings", { [field]: draft });
      toast.success("Saved");
      await refresh();
      setOpen(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const Tag = as;

  if (!editing) {
    if (!value) return null; // Public visitors see nothing for empty fields
    return <Tag className={className} data-testid={testId}>{value}</Tag>;
  }

  if (open) {
    if (multiline) {
      return (
        <div className="space-y-2">
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            rows={4}
            className="w-full bg-[color:var(--bda-surface)] border border-[color:var(--bda-blue-glow)]/40 rounded-md px-3 py-2 text-sm text-[color:var(--bda-text)]"
          />
          <div className="flex items-center gap-2 justify-center">
            <button onClick={save} disabled={saving} className="px-3 py-1.5 rounded-md bg-[color:var(--bda-blue-glow)] text-black text-xs font-medium">
              {saving ? "Saving…" : "Save"}
            </button>
            <button onClick={() => { setDraft(value || ""); setOpen(false); }} className="px-3 py-1.5 rounded-md border border-[color:var(--bda-border)] text-xs text-[color:var(--bda-text-muted)]">
              Cancel
            </button>
          </div>
        </div>
      );
    }
    return (
      <div className="inline-flex items-center gap-2">
        <input
          autoFocus
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") save(); if (e.key === "Escape") setOpen(false); }}
          onBlur={save}
          className="bg-[color:var(--bda-surface)] border border-[color:var(--bda-blue-glow)]/40 rounded-md px-2 py-1 text-sm text-[color:var(--bda-text)]"
        />
        {saving && <Loader2 className="w-3.5 h-3.5 animate-spin text-[color:var(--bda-blue-glow)]" />}
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={() => setOpen(true)}
      data-testid={testId ? `${testId}-edit` : undefined}
      className={`group/edit relative inline-flex items-center gap-1.5 ${className}`}
      title="Click to edit"
    >
      <Tag>{value || <span className="opacity-60 italic">{placeholder || "Click to add"}</span>}</Tag>
      <Pencil className="w-3 h-3 opacity-0 group-hover/edit:opacity-70 transition-opacity text-[color:var(--bda-blue-glow)]" />
    </button>
  );
}

/**
 * Inline-editable image bound to a settings field — uploads on file pick.
 */
export function EditableImage({ field, value, alt, className = "", imgClassName = "", testId }) {
  const { editing } = useAdmin();
  const { refresh } = useSettings();
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const onPick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd);
      await api.patch("/settings", { [field]: data.url });
      toast.success("Image updated");
      await refresh();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setUploading(false); }
  };

  return (
    <div className={`relative ${className}`} data-testid={testId}>
      <img src={fileUrl(value)} alt={alt} className={imgClassName} />
      {editing && (
        <>
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            className="absolute inset-0 flex items-center justify-center bg-black/0 hover:bg-black/55 transition-colors"
            title="Replace image"
            data-testid={testId ? `${testId}-edit` : undefined}
          >
            <span className="opacity-0 hover:opacity-100 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/95 text-black text-xs font-medium">
              {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
              {uploading ? "Uploading…" : "Replace"}
            </span>
          </button>
          <input ref={inputRef} type="file" accept="image/*" onChange={onPick} className="hidden" />
        </>
      )}
    </div>
  );
}
